// EBPristjek/EBPristjek.tsx
import { useEffect, useMemo, useState } from "react"
import { Row, Col, Button, Form, Spinner, Table, Modal, Tabs, Tab, Alert, Card, InputGroup } from "react-bootstrap"
import { useLocation, useNavigate } from "react-router-dom"
import DataTable, { TableColumn } from "react-data-table-component"
import ArticleTab from "./article-tab"
import ProductFetcher from "./product-fetcher"
import useFetch from "../../../_fetch"
import CsvFileList from "./csv-file-list"

type ProductRow = {
  name: string
  ean: string
}

type RabatkodeCategoryRow = {
  id: number
  name: string
  slug: string
  count: number
  description: string
}

type RabatkodePostRow = {
  id: number
  title: string
  name: string
  slug: string
  date: string
  status: string
  link: string
  code: string
  description: string
  valid_until: string
  conditions: string
  rabatkode_kategori: number[]
}

type IGenerating = {
  feedGenerating: boolean
  googleUpdateRunning: boolean
  wpBrandUpdateRunning: boolean
  wpProductUpdateRunning: boolean
  isAddingCategory: boolean
}

export default function EBPristjek() {
  const { doFetch } = useFetch()
  const location = useLocation()
  const navigate = useNavigate()

  const getTabFromPath = (pathname: string) => {
    if (pathname.startsWith("/eb-pristjek/articles")) return "articles"
    if (pathname.startsWith("/eb-pristjek/rabatkoder")) return "rabatkoder"
    return "products"
  }

  const getCategoryIdFromPath = (pathname: string) => {
    const match = pathname.match(/^\/eb-pristjek\/rabatkoder\/(\d+)$/)
    return Number(match?.[1] || 0)
  }

  const [csvFiles, setCsvFiles] = useState<string[]>([])
  const [activeTab, setActiveTab] = useState<string>(getTabFromPath(location.pathname))
  const [productRows, setProductRows] = useState<ProductRow[]>([{ name: "", ean: "" }])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isGeneratingFile, setIsGeneratingFile] = useState({ feedGenerating: false, googleUpdateRunning: false, wpBrandUpdateRunning: false, wpProductUpdateRunning: false, isAddingCategory: false })
  const [rabatkodeCategories, setRabatkodeCategories] = useState<RabatkodeCategoryRow[]>([])
  const [isLoadingRabatkoder, setIsLoadingRabatkoder] = useState(false)
  const [isCreatingRabatkode, setIsCreatingRabatkode] = useState(false)
  const [rabatkodeError, setRabatkodeError] = useState("")
  const [rabatkodeSuccess, setRabatkodeSuccess] = useState("")
  const [selectedRabatkodeCategory, setSelectedRabatkodeCategory] = useState<RabatkodeCategoryRow | null>(null)
  const [rabatkodePosts, setRabatkodePosts] = useState<RabatkodePostRow[]>([])
  const [isLoadingRabatkodePosts, setIsLoadingRabatkodePosts] = useState(false)
  const [isCreatingRabatkodePost, setIsCreatingRabatkodePost] = useState(false)
  const [isGeneratingRabatkodeArticle, setIsGeneratingRabatkodeArticle] = useState(false)
  const [rabatkodePostError, setRabatkodePostError] = useState("")
  const [rabatkodePostSuccess, setRabatkodePostSuccess] = useState("")
  const [rabatkodeArticleError, setRabatkodeArticleError] = useState("")
  const [rabatkodeArticleSuccess, setRabatkodeArticleSuccess] = useState("")
  const [categorySearch, setCategorySearch] = useState("")
  const [postSearch, setPostSearch] = useState("")
  const [rabatkodePostForm, setRabatkodePostForm] = useState({
    title: "",
    code: "",
    description: "",
    validUntil: "",
    conditions: "",
  })
  const [rabatkodeForm, setRabatkodeForm] = useState({
    name: "",
    redirectUrl: "",
    imageDataUrl: "",
    imageFileName: "",
  })
  const readFileAsDataUrl = (file: File) => {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(String(reader.result || ""))
      reader.onerror = () => reject(new Error("Kunne ikke læse billedefilen"))
      reader.readAsDataURL(file)
    })
  }

  const handleRabatkodeImageChange = async (file?: File) => {
    if (!file) {
      setRabatkodeForm((prev) => ({ ...prev, imageDataUrl: "", imageFileName: "" }))
      return
    }

    if (!String(file.type || "").startsWith("image/")) {
      setRabatkodeError("Vælg en gyldig billedfil (jpg, png, webp, gif).")
      return
    }

    if (file.size > 6 * 1024 * 1024) {
      setRabatkodeError("Billedfilen er for stor. Maks størrelse er 6 MB.")
      return
    }

    try {
      setRabatkodeError("")
      const imageDataUrl = await readFileAsDataUrl(file)
      setRabatkodeForm((prev) => ({
        ...prev,
        imageDataUrl,
        imageFileName: file.name,
      }))
    } catch (err) {
      console.error("Fejl ved læsning af billedfil:", err)
      setRabatkodeError("Kunne ikke læse billedfilen.")
    }
  }


const [newCategory, setNewCategory] = useState("")
  const [confirmModal, setConfirmModal] = useState<{ show: boolean; label: string; onConfirm: () => void }>({ show: false, label: "", onConfirm: () => {} })
  const isProductsTab = activeTab === "products"

  const askConfirm = (label: string, action: () => void) => {
    setConfirmModal({ show: true, label, onConfirm: action })
  }

  const closeConfirm = () => setConfirmModal(prev => ({ ...prev, show: false }))
const handleAddCategory = async () => {
  if (!newCategory.trim()) return
  setIsGeneratingFile(prev => ({ ...prev, isAddingCategory: true }))
  try {
    await doFetch("/eb-pristjek/add-category", { category: newCategory })

  } catch (err) {
    console.error("Fejl ved tilføj kategori:", err)
  }
}

  const fetchRabatkodeCategories = async () => {
    setIsLoadingRabatkoder(true)
    setRabatkodeError("")
    try {
      const res = await doFetch("/eb-pristjek/rabatkode-categories")
      const data = await res.json()
      const categories = Array.isArray(data) ? data : []
      setRabatkodeCategories(categories)
    } catch (err) {
      console.error("Fejl ved hentning af rabatkoder:", err)
      setRabatkodeError("Kunne ikke hente rabatkodekategorier.")
    } finally {
      setIsLoadingRabatkoder(false)
    }
  }

  const handleCreateRabatkodeCategory = async () => {
    const trimmedName = rabatkodeForm.name.trim()
    const trimmedRedirectUrl = rabatkodeForm.redirectUrl.trim().replace(/\/+$/, "")
    const imageDataUrl = String(rabatkodeForm.imageDataUrl || "").trim()

    if (!trimmedName || !trimmedRedirectUrl || !imageDataUrl) {
      setRabatkodeError("Udfyld navn, redirect URL og upload et billede/logo.")
      return
    }

    setIsCreatingRabatkode(true)
    setRabatkodeError("")
    setRabatkodeSuccess("")

    try {
      const res = await doFetch("/eb-pristjek/rabatkode-categories", {
        name: trimmedName,
        redirectUrl: trimmedRedirectUrl,
        imageUrl: imageDataUrl,
      })

      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}))
        throw new Error(errBody?.error || `HTTP ${res.status}`)
      }

      setRabatkodeForm({ name: "", redirectUrl: "", imageDataUrl: "", imageFileName: "" })
      setRabatkodeSuccess(`Rabatkode-kategori oprettet: ${trimmedName}`)
      await fetchRabatkodeCategories()
    } catch (err) {
      console.error("Fejl ved oprettelse af rabatkodekategori:", err)
      setRabatkodeError(`Kunne ikke oprette rabatkodekategorien. ${err instanceof Error ? err.message : ""}`.trim())
    } finally {
      setIsCreatingRabatkode(false)
    }
  }

  const generateSlug = (value: string) => {
    return String(value || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9\s-]/g, "")
      .trim()
      .replace(/\s+/g, "-")
  }

  const fetchRabatkodePosts = async (categoryId: number) => {
    setIsLoadingRabatkodePosts(true)
    setRabatkodePostError("")
    try {
      const res = await doFetch(`/eb-pristjek/rabatkode-posts?categoryId=${categoryId}`)
      const data = await res.json()
      setRabatkodePosts(Array.isArray(data) ? data : [])
    } catch (err) {
      console.error("Fejl ved hentning af rabatkoder i kategori:", err)
      setRabatkodePostError("Kunne ikke hente rabatkoder i kategorien.")
    } finally {
      setIsLoadingRabatkodePosts(false)
    }
  }

  const openCategoryRabatkoder = async (category: RabatkodeCategoryRow, shouldNavigate = true) => {
    if (shouldNavigate) {
      navigate(`/eb-pristjek/rabatkoder/${category.id}`)
    }
    setSelectedRabatkodeCategory(category)
    setRabatkodeArticleSuccess("")
    setRabatkodeArticleError("")
    setRabatkodePostSuccess("")
    setRabatkodePostError("")
    setRabatkodePostForm({ title: "", code: "", description: "", validUntil: "", conditions: "" })
    await fetchRabatkodePosts(category.id)
  }

  const handleGenerateRabatkodeArticle = async () => {
    if (!selectedRabatkodeCategory) return

    setIsGeneratingRabatkodeArticle(true)
    setRabatkodeArticleError("")
    setRabatkodeArticleSuccess("")

    try {
      await doFetch(`/eb-pristjek/rabatkode-categories/${selectedRabatkodeCategory.id}/article`, {})
      setRabatkodeArticleSuccess(`SEO-tekst opdateret for ${selectedRabatkodeCategory.name}`)
      await fetchRabatkodeCategories()
    } catch (err) {
      console.error("Fejl ved generering af rabatkode-artikel:", err)
      setRabatkodeArticleError("Kunne ikke generere artikelteksten for kategorien.")
    } finally {
      setIsGeneratingRabatkodeArticle(false)
    }
  }

  const handleCreateRabatkodePost = async () => {
    if (!selectedRabatkodeCategory) return

    const trimmedTitle = rabatkodePostForm.title.trim()
    const trimmedCode = rabatkodePostForm.code.trim()
    const trimmedDescription = rabatkodePostForm.description.trim()
    const trimmedValidUntil = rabatkodePostForm.validUntil.trim()
    const trimmedConditions = rabatkodePostForm.conditions.trim()

    if (!trimmedTitle) {
      setRabatkodePostError("Title er påkrævet.")
      return
    }

    if (!trimmedCode) {
      setRabatkodePostError("Code er påkrævet.")
      return
    }

    setIsCreatingRabatkodePost(true)
    setRabatkodePostError("")
    setRabatkodePostSuccess("")

    try {
      await doFetch("/eb-pristjek/rabatkode-posts", {
        title: trimmedTitle,
        name: trimmedTitle,
        code: trimmedCode,
        description: trimmedDescription,
        validUntil: trimmedValidUntil,
        conditions: trimmedConditions,
        categoryId: selectedRabatkodeCategory.id,
      })

      setRabatkodePostForm({ title: "", code: "", description: "", validUntil: "", conditions: "" })
      setRabatkodePostSuccess(`Rabatkode oprettet i ${selectedRabatkodeCategory.name}: ${trimmedTitle}`)
      await fetchRabatkodePosts(selectedRabatkodeCategory.id)
      await fetchRabatkodeCategories()
    } catch (err) {
      console.error("Fejl ved oprettelse af rabatkode:", err)
      setRabatkodePostError("Kunne ikke oprette rabatkoden.")
    } finally {
      setIsCreatingRabatkodePost(false)
    }
  }

  useEffect(() => {
    if (!isProductsTab) return

    const fetchCsvFiles = async () => {
      const res = await doFetch("/eb-pristjek/get-feeds")
      const data:string[] = await res.json()
      const filesSorted = data.sort((a: string, b: string) => {
        const [aY, aM, aD, aH, aMin, aS] = a.split("_")[0].split('-')
        const [bY, bM, bD, bH, bMin, bS] = b.split("_")[0].split('-')
        const aDate = new Date(`${aY}-${aM}-${aD} ${aH}:${aMin}:${aS}`)
        const bDate = new Date(`${bY}-${bM}-${bD} ${bH}:${bMin}:${bS}`)

        return bDate.getTime() - aDate.getTime()
      })

      setCsvFiles(filesSorted)
    }
    fetchCsvFiles()
  }, [doFetch, isProductsTab])

  useEffect(() => {
    if (!isProductsTab) return

    const checkStatus = async () => {
      const res = await doFetch("/eb-pristjek/is-generating")
      const data = await res.json() as IGenerating
      setIsGeneratingFile(data)
    }
    checkStatus()
  }, [doFetch, isProductsTab])

  useEffect(() => {
    fetchRabatkodeCategories()
  }, [])

  useEffect(() => {
    setActiveTab(getTabFromPath(location.pathname))

    const categoryId = getCategoryIdFromPath(location.pathname)
    if (categoryId > 0 && rabatkodeCategories.length) {
      const found = rabatkodeCategories.find((c) => c.id === categoryId)
      if (found && selectedRabatkodeCategory?.id !== found.id) {
        openCategoryRabatkoder(found, false)
      }
      return
    }

    if (categoryId <= 0 && selectedRabatkodeCategory) {
      setSelectedRabatkodeCategory(null)
      setRabatkodePosts([])
      setRabatkodeArticleError("")
      setRabatkodeArticleSuccess("")
      setRabatkodePostError("")
      setRabatkodePostSuccess("")
    }
  }, [location.pathname, rabatkodeCategories])

  useEffect(() => {
    if (!isProductsTab || !isGeneratingFile) return

    const interval = setInterval(async () => {
      try {
        const res = await doFetch("/eb-pristjek/is-generating")
        const data = await res.json() as IGenerating
        if (!data.feedGenerating && !data.googleUpdateRunning && !data.wpBrandUpdateRunning && !data.wpProductUpdateRunning && !data.isAddingCategory) {
          const csvResponse = await doFetch("/eb-pristjek/get-feeds")
          const files = await csvResponse.json()
          const filesSorted = files.sort((a: string, b: string) => {
            const [aY, aM, aD, aH, aMin, aS] = a.split("_")[0].split('-')
            const [bY, bM, bD, bH, bMin, bS] = b.split("_")[0].split('-')
            const aDate = new Date(`${aY}-${aM}-${aD} ${aH}:${aMin}:${aS}`)
            const bDate = new Date(`${bY}-${bM}-${bD} ${bH}:${bMin}:${bS}`)

            return bDate.getTime() - aDate.getTime()
          })
          setCsvFiles(filesSorted)
          setIsGeneratingFile(data)
          clearInterval(interval)
        }
      } catch (err) {
        console.error("Fejl ved check:", err)
      }
    }, 10000)

    return () => clearInterval(interval)
  }, [doFetch, isGeneratingFile, isProductsTab])

  const handleRowChange = (index: number, field: keyof ProductRow, value: string) => {
    setProductRows(prev => {
      const next = [...prev]
      next[index] = { ...next[index], [field]: value }
      return next
    })
  }

  const handleAddRow = () => {
    setProductRows(prev => [...prev, { name: "", ean: "" }])
  }

  const handleDeleteRow = (index: number) => {
    setProductRows(prev => {
      const next = prev.filter((_, i) => i !== index)
      return next.length ? next : [{ name: "", ean: "" }]
    })
  }

  const handleDeleteFile = async (fileName: string) => {
    try {
      await doFetch("/eb-pristjek/delete-feed", { file: fileName })
      setCsvFiles(prev => prev.filter(f => f !== fileName))
    } catch (err) {
      console.error("Kunne ikke slette fil:", err)
    }
  }

  const onDownload = async (fileName: string) => {
    try {
      const res = await doFetch("/eb-pristjek/download-feed/" + fileName)
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = fileName
      a.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error("Kunne ikke download fil:", err)
      alert(err)
    }
  }

  const handleSubmit = async () => {
    setIsSubmitting(true)
    setIsGeneratingFile({ feedGenerating: true, googleUpdateRunning: false, wpBrandUpdateRunning: false, wpProductUpdateRunning: false, isAddingCategory: false })

    try {
      const products = productRows
        .map((p) => ({
          name: p.name.trim(),
          ean: p.ean.trim(),
        }))
        .filter((p) => p.name && p.ean)

      if (!products.length) {
        throw new Error("Tilføj mindst ét produkt med både navn og EAN/PR-ID.")
      }

      await doFetch("/eb-pristjek/generate-product-feed", { products })
    } catch (err) {
      alert(`${err}`)
    } finally {
      setIsSubmitting(false)
    }
  }

  const rowCount = productRows
    .map((p) => ({ name: p.name.trim(), ean: p.ean.trim() }))
    .filter((p) => p.name && p.ean).length

  const costUSD = (rowCount * 0.002).toFixed(2)
  const priceRunnerRequests = Math.ceil(rowCount / 10)
  const hasValidRows = rowCount > 0

  const handleUpdateGoogleProducts = async (fullUpdate: boolean) => {
    setIsGeneratingFile(prev => ({ ...prev, googleUpdateRunning: true }))
    try {
      await doFetch(`/eb-pristjek/update-google-products`, { fullUpdate })
      setIsGeneratingFile(prev => ({ ...prev, googleUpdateRunning: true }))
    } catch (err) {
      console.error("Fejl ved Google update:", err)
    }
  }

  const handleUpdateWpBrands = async () => {
    setIsGeneratingFile(prev => ({ ...prev, wpBrandUpdateRunning: true }))
    try {
      await doFetch("/eb-pristjek/update-wp-brands", {})
      setIsGeneratingFile(prev => ({ ...prev, wpBrandUpdateRunning: true }))
    } catch (err) {
      console.error("Fejl ved WP Brands update:", err)
    }
  }

  const handleUpdateWpProducts = async () => {
    setIsGeneratingFile(prev => ({ ...prev, wpProductUpdateRunning: true }))
    try {
      await doFetch("/eb-pristjek/update-wp-products", {})
      setIsGeneratingFile(prev => ({ ...prev, wpProductUpdateRunning: true }))
    } catch (err) {
      console.error("Fejl ved WP Products update:", err)
    }
  }

  const isBusy =
    isGeneratingFile.feedGenerating ||
    isGeneratingFile.googleUpdateRunning ||
    isGeneratingFile.wpBrandUpdateRunning ||
    isGeneratingFile.wpProductUpdateRunning ||
    isGeneratingFile.isAddingCategory ||
    isSubmitting

  const tableStyles = useMemo(() => ({
    headCells: {
      style: {
        fontWeight: 700,
        fontSize: "12px",
        letterSpacing: "0.02em",
        textTransform: "uppercase" as const,
        backgroundColor: "#f8f9fa",
      },
    },
    cells: {
      style: {
        fontSize: "14px",
        paddingTop: "12px",
        paddingBottom: "12px",
      },
    },
    rows: {
      style: {
        minHeight: "54px",
      },
    },
  }), [])

  const filteredRabatkodeCategories = useMemo(() => {
    const q = categorySearch.trim().toLowerCase()
    if (!q) return rabatkodeCategories

    return rabatkodeCategories.filter((category) => {
      return [
        String(category.id),
        category.name,
        category.slug,
        String(category.count),
      ].some((value) => String(value || "").toLowerCase().includes(q))
    })
  }, [rabatkodeCategories, categorySearch])

  const filteredRabatkodePosts = useMemo(() => {
    const q = postSearch.trim().toLowerCase()
    if (!q) return rabatkodePosts

    return rabatkodePosts.filter((post) => {
      return [
        String(post.id),
        post.title,
        post.name,
        post.code,
        post.slug,
        post.status,
        post.valid_until,
        post.conditions,
        post.description,
      ].some((value) => String(value || "").toLowerCase().includes(q))
    })
  }, [rabatkodePosts, postSearch])

  const categoryColumns: TableColumn<RabatkodeCategoryRow>[] = useMemo(() => [
    {
      name: "ID",
      selector: (row) => row.id,
      sortable: true,
      width: "90px",
    },
    {
      name: "Navn",
      selector: (row) => row.name,
      sortable: true,
      grow: 2,
      wrap: true,
    },
    {
      name: "Slug",
      selector: (row) => row.slug,
      sortable: true,
      grow: 2,
      wrap: true,
    },
    {
      name: "Antal",
      selector: (row) => row.count,
      sortable: true,
      right: true,
      width: "110px",
    },
    {
      name: "Detaljer",
      width: "140px",
      cell: (row) => (
        <Button
          size="sm"
          variant="outline-primary"
          onClick={() => openCategoryRabatkoder(row)}
        >
          Åbn
        </Button>
      ),
    },
  ], [openCategoryRabatkoder])

  const postColumns: TableColumn<RabatkodePostRow>[] = useMemo(() => [
    {
      name: "ID",
      selector: (row) => row.id,
      sortable: true,
      width: "80px",
    },
    {
      name: "Title / Name",
      selector: (row) => row.name || row.title || "-",
      sortable: true,
      grow: 2,
      wrap: true,
    },
    {
      name: "Code",
      selector: (row) => row.code || "-",
      sortable: true,
      width: "110px",
    },
    {
      name: "Valid until",
      selector: (row) => row.valid_until || "-",
      sortable: true,
      width: "120px",
    },
    {
      name: "Conditions",
      grow: 1.2,
      cell: (row) => {
        const value = row.conditions || "-"
        return (
          <div
            title={value}
            style={{
              width: "100%",
              maxWidth: "180px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {value}
          </div>
        )
      },
    },
    {
      name: "Description",
      grow: 1.5,
      cell: (row) => {
        const value = row.description || "-"
        return (
          <div
            title={value}
            style={{
              width: "100%",
              maxWidth: "240px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {value}
          </div>
        )
      },
    },
    {
      name: "Slug",
      grow: 1.1,
      cell: (row) => {
        const value = row.slug || "-"
        return (
          <div
            title={value}
            style={{
              width: "100%",
              maxWidth: "180px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {value}
          </div>
        )
      },
      sortable: true,
    },
    {
      name: "Status",
      selector: (row) => row.status,
      sortable: true,
      width: "95px",
    },
  ], [])

  return (
    <div>
      <h3>EB Pristjek</h3>
      <Tabs
        activeKey={activeTab}
        onSelect={(key) => {
          const next = key || "products"
          setActiveTab(next)
          if (next === "products") {
            navigate('/eb-pristjek/products')
            return
          }

          if (next === "articles") {
            navigate('/eb-pristjek/articles')
            return
          }

          if (selectedRabatkodeCategory?.id) {
            navigate(`/eb-pristjek/rabatkoder/${selectedRabatkodeCategory.id}`)
            return
          }

          navigate('/eb-pristjek/rabatkoder')
        }}
        id="eb-pristjek-tabs"
        className="mt-4 mb-3"
      >
        <Tab eventKey="products" title="Produkter">
          <Row className="mt-3">
            <Col md={3}>
              <h5>Genererede CSV-filer</h5>
              <CsvFileList
                files={csvFiles}
                isGenerating={isGeneratingFile.feedGenerating}
                onDelete={handleDeleteFile}
                onDownload={onDownload}
              />

              <div className="mt-4 d-flex flex-column gap-2">
                <Button
                  variant="info"
                  disabled={isBusy}
                  onClick={() => askConfirm("Opdater Google", () => handleUpdateGoogleProducts(false))}
                >
                  {isGeneratingFile.googleUpdateRunning ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Opdaterer Google
                    </>
                  ) : (
                    "Opdater Google"
                  )}
                </Button>
                <Button
                  variant="secondary"
                  disabled={isBusy}
                  onClick={() => askConfirm("Opdater WP Brands", handleUpdateWpBrands)}
                >
                  {isGeneratingFile.wpBrandUpdateRunning ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Opdaterer WP Brands...
                    </>
                  ) : (
                    "Opdater WP Brands"
                  )}
                </Button>
                <Button
                  variant="secondary"
                  disabled={isBusy}
                  onClick={() => askConfirm("Opdater WP Products", handleUpdateWpProducts)}
                >
                  {isGeneratingFile.wpProductUpdateRunning ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Opdaterer WP Products...
                    </>
                  ) : (
                    "Opdater WP Products"
                  )}
                </Button>
                <Form.Control
                  type="text"
                  placeholder="Indtast kategori"
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                />
                <Button
                  variant="success"
                  disabled={isBusy || !newCategory.trim()}
                  onClick={() => askConfirm(`Tilføj kategori "${newCategory}"`, handleAddCategory)}
                >
                  {isGeneratingFile.isAddingCategory ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Tilføjer kategori...
                    </>
                  ) : (
                    "Tilføj kategori"
                  )}
                </Button>
              </div>
            </Col>
            <Col md={9}>
              <ProductFetcher
                onProductsProcessed={(products) => {
                  const mapped = products.map((p) => ({
                    name: p.name || "",
                    ean: p.ean || "",
                  }))
                  setProductRows(mapped.length ? mapped : [{ name: "", ean: "" }])
                }}
              />

              <Form.Group className="mb-3 mt-3" controlId="productTable">
                <Form.Label>Produkter til oprettelse</Form.Label>
                <div style={{ maxHeight: "480px", overflowY: "auto" }}>
                  <Table striped bordered hover size="sm" className="mb-0 align-middle">
                    <thead>
                      <tr>
                        <th style={{ width: "55%" }} className="py-2">Produktnavn</th>
                        <th style={{ width: "35%" }} className="py-2">EAN / PR-ID</th>
                        <th style={{ width: "10%" }} className="py-2 text-center">Handling</th>
                      </tr>
                    </thead>
                    <tbody>
                      {productRows.map((row, index) => (
                        <tr key={index}>
                          <td className="p-0" style={{ height: "44px" }}>
                            <Form.Control
                              type="text"
                              value={row.name}
                              placeholder="fx Apple AirPods 4"
                              onChange={(e) => handleRowChange(index, "name", e.target.value)}
                              size="sm"
                              className="h-100 border-0 rounded-0"
                              style={{ boxShadow: "none" }}
                            />
                          </td>
                          <td className="p-0" style={{ height: "44px" }}>
                            <Form.Control
                              type="text"
                              value={row.ean}
                              placeholder="fx 5713552024299 eller pr_3457406272"
                              onChange={(e) => handleRowChange(index, "ean", e.target.value)}
                              size="sm"
                              className="h-100 border-0 rounded-0"
                              style={{ boxShadow: "none" }}
                            />
                          </td>
                          <td className="p-0" style={{ height: "44px" }}>
                            <Button
                              variant="danger"
                              className="w-100 h-100 rounded-0 border-0 fw-semibold"
                              onClick={() => handleDeleteRow(index)}
                            >
                              Slet
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
                <div className="mt-2 d-flex justify-content-between">
                  <Button variant="outline-primary" size="sm" onClick={handleAddRow}>
                    Tilføj række
                  </Button>
                  <div className="text-muted small align-self-center">
                    Gyldige rækker: {rowCount}
                  </div>
                </div>
              </Form.Group>

              <Button
                variant="primary"
                onClick={handleSubmit}
                disabled={isGeneratingFile.feedGenerating || isGeneratingFile.googleUpdateRunning || isGeneratingFile.wpBrandUpdateRunning || isGeneratingFile.wpProductUpdateRunning || isSubmitting || !hasValidRows}
              >
                {isSubmitting ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Indsender...
                  </>
                ) : (
                  "Indsend"
                )}
              </Button>

              <div className="text-muted" style={{ lineHeight: "1.5" }}>
                <div>
                  Dette opslag koster cirka <strong>${costUSD}</strong>. Se forbrug:{" "}
                  <a
                    href="https://platform.openai.com/usage"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    platform.openai.com/usage
                  </a>
                </div>
                <div>
                  <strong>{rowCount > 0 ? priceRunnerRequests : 0}</strong> API request
                  {priceRunnerRequests !== 1 ? "s" : ""} mod PriceRunner.
                </div>
                <div>
                  Understøttede værdier i EAN-feltet: almindelig EAN eller PR-ID i formatet <code>pr_1234567890</code>.
                </div>
              </div>
            </Col>
          </Row>
        </Tab>

        <Tab eventKey="rabatkoder" title="Rabatkoder">
          {!selectedRabatkodeCategory ? (
            <Row className="mt-3 g-3">
              <Col xl={4}>
                <Card className="border-0 shadow-sm">
                  <Card.Body>
                    <h5 className="mb-3">Opret rabatkodekategori</h5>
                    <Form.Group className="mb-3" controlId="rabatkodeName">
                      <Form.Label>Navn</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="fx Boozt"
                        value={rabatkodeForm.name}
                        onChange={(e) => setRabatkodeForm((prev) => ({ ...prev, name: e.target.value }))}
                      />
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="rabatkodeRedirectUrl">
                      <Form.Label>Affiliate/Redirct link</Form.Label>
                      <Form.Control
                        type="url"
                        placeholder="https://www.eksempel.dk"
                        value={rabatkodeForm.redirectUrl}
                        onChange={(e) => setRabatkodeForm((prev) => ({ ...prev, redirectUrl: e.target.value }))}
                      />
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="rabatkodeImageUpload">
                      <Form.Label>Billede/logo</Form.Label>
                      <Form.Control
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleRabatkodeImageChange((e.target as HTMLInputElement).files?.[0])}
                      />
                      <Form.Text className="text-muted">
                        {rabatkodeForm.imageFileName ? `Valgt fil: ${rabatkodeForm.imageFileName}` : "Ingen fil valgt endnu"}
                      </Form.Text>
                    </Form.Group>

                    <div className="d-flex flex-wrap gap-2">
                      <Button
                        variant="primary"
                        disabled={isCreatingRabatkode}
                        onClick={handleCreateRabatkodeCategory}
                      >
                        {isCreatingRabatkode ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Opretter...
                          </>
                        ) : (
                          "Opret kategori"
                        )}
                      </Button>
                      <Button
                        variant="outline-secondary"
                        disabled={isLoadingRabatkoder || isCreatingRabatkode}
                        onClick={fetchRabatkodeCategories}
                      >
                        Opdater liste
                      </Button>
                    </div>

                    {rabatkodeError ? <Alert className="mt-3 mb-0" variant="danger">{rabatkodeError}</Alert> : null}
                    {rabatkodeSuccess ? <Alert className="mt-3 mb-0" variant="success">{rabatkodeSuccess}</Alert> : null}
                  </Card.Body>
                </Card>
              </Col>

              <Col xl={8}>
                <Card className="border-0 shadow-sm">
                  <Card.Body>
                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                      <h5 className="mb-0">Eksisterende rabatkodekategorier</h5>
                      {isLoadingRabatkoder ? <Spinner animation="border" size="sm" /> : null}
                    </div>

                    <DataTable
                      columns={categoryColumns}
                      data={filteredRabatkodeCategories}
                      customStyles={tableStyles}
                      progressPending={isLoadingRabatkoder}
                      highlightOnHover
                      responsive
                      pagination
                      fixedHeader
                      fixedHeaderScrollHeight="52vh"
                      persistTableHead
                      noDataComponent="Ingen rabatkodekategorier fundet."
                      subHeader
                      subHeaderComponent={
                        <InputGroup className="w-100" style={{ maxWidth: "420px" }}>
                          <InputGroup.Text>Søg</InputGroup.Text>
                          <Form.Control
                            placeholder="Søg på ID, navn, slug eller antal"
                            value={categorySearch}
                            onChange={(e) => setCategorySearch(e.target.value)}
                          />
                        </InputGroup>
                      }
                    />
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          ) : (
            <Row className="mt-3 g-3">
              <Col xs={12}>
                <Card className="border-0 shadow-sm">
                  <Card.Body className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                    <div className="d-flex align-items-center gap-3">
                      <Button
                        variant="outline-secondary"
                        onClick={() => {
                          navigate('/eb-pristjek/rabatkoder')
                        }}
                      >
                        ← Tilbage til kategorier
                      </Button>
                      <div>
                        <h5 className="mb-1">Rabatkoder i {selectedRabatkodeCategory.name}</h5>
                        <small className="text-muted">Kategori ID: {selectedRabatkodeCategory.id} · Slug: {selectedRabatkodeCategory.slug}</small>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              <Col lg={5}>
                <Card className="border-0 shadow-sm mb-3">
                  <Card.Body>
                    <h6 className="mb-2">SEO-artikel til kategorisiden</h6>
                    <p className="text-muted mb-3">
                      Genererer ny kategori-tekst, sidebar-indhold og Yoast-felter baseret på brandet og de nuværende rabatkoder i kategorien.
                    </p>

                    <div className="d-flex flex-wrap gap-2 align-items-center">
                      <Button
                        variant="dark"
                        disabled={isGeneratingRabatkodeArticle}
                        onClick={handleGenerateRabatkodeArticle}
                      >
                        {isGeneratingRabatkodeArticle ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Genererer...
                          </>
                        ) : (
                          "Generer artikel"
                        )}
                      </Button>
                    </div>

                    {rabatkodeArticleError ? <Alert className="mt-3 mb-0" variant="danger">{rabatkodeArticleError}</Alert> : null}
                    {rabatkodeArticleSuccess ? <Alert className="mt-3 mb-0" variant="success">{rabatkodeArticleSuccess}</Alert> : null}
                  </Card.Body>
                </Card>

                <Card className="border-0 shadow-sm">
                  <Card.Body>
                    <h6 className="mb-3">Tilføj ny rabatkode</h6>
                    <Row>
                      <Col md={5}>
                        <Form.Group className="mb-2" controlId="rabatkodePostTitle">
                          <Form.Label>Title / Name</Form.Label>
                          <Form.Control
                            type="text"
                            value={rabatkodePostForm.title}
                            onChange={(e) => setRabatkodePostForm((prev) => ({ ...prev, title: e.target.value }))}
                            placeholder="fx 20% rabat"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group className="mb-2" controlId="rabatkodePostCode">
                          <Form.Label>Code</Form.Label>
                          <Form.Control
                            type="text"
                            value={rabatkodePostForm.code}
                            onChange={(e) => setRabatkodePostForm((prev) => ({ ...prev, code: e.target.value }))}
                            placeholder="fx SPAR20"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-2" controlId="rabatkodePostValidUntil">
                          <Form.Label>Valid until (custom felt)</Form.Label>
                          <Form.Control
                            type="date"
                            value={rabatkodePostForm.validUntil}
                            onChange={(e) => setRabatkodePostForm((prev) => ({ ...prev, validUntil: e.target.value }))}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-2" controlId="rabatkodePostConditions">
                          <Form.Label>Conditions (custom felt)</Form.Label>
                          <Form.Control
                            type="text"
                            value={rabatkodePostForm.conditions}
                            onChange={(e) => setRabatkodePostForm((prev) => ({ ...prev, conditions: e.target.value }))}
                            placeholder="fx Gælder kun nye kunder"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={12}>
                        <Form.Group className="mb-2" controlId="rabatkodePostDescription">
                          <Form.Label>Description (custom felt)</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            value={rabatkodePostForm.description}
                            onChange={(e) => setRabatkodePostForm((prev) => ({ ...prev, description: e.target.value }))}
                            placeholder="Kort beskrivelse af rabatkoden"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={12}>
                        <Form.Group className="mb-2" controlId="rabatkodePostSlugPreview">
                          <Form.Label>Slug (auto)</Form.Label>
                          <Form.Control
                            type="text"
                            value={generateSlug(rabatkodePostForm.title)}
                            readOnly
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <div className="d-flex flex-wrap gap-2 align-items-center">
                      <Button
                        variant="primary"
                        disabled={isCreatingRabatkodePost}
                        onClick={handleCreateRabatkodePost}
                      >
                        {isCreatingRabatkodePost ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Opretter...
                          </>
                        ) : (
                          "Opret rabatkode"
                        )}
                      </Button>
                      <Button
                        variant="outline-secondary"
                        disabled={isLoadingRabatkodePosts || isCreatingRabatkodePost}
                        onClick={() => fetchRabatkodePosts(selectedRabatkodeCategory.id)}
                      >
                        Opdater rabatkoder
                      </Button>
                    </div>

                    {rabatkodePostError ? <Alert className="mt-3 mb-0" variant="danger">{rabatkodePostError}</Alert> : null}
                    {rabatkodePostSuccess ? <Alert className="mt-3 mb-0" variant="success">{rabatkodePostSuccess}</Alert> : null}
                  </Card.Body>
                </Card>
              </Col>

              <Col lg={7}>
                <Card className="border-0 shadow-sm">
                  <Card.Body>
                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                      <h6 className="mb-0">Eksisterende rabatkoder</h6>
                      {isLoadingRabatkodePosts ? <Spinner animation="border" size="sm" /> : null}
                    </div>

                    <DataTable
                      columns={postColumns}
                      data={filteredRabatkodePosts}
                      customStyles={tableStyles}
                      progressPending={isLoadingRabatkodePosts}
                      highlightOnHover
                      responsive
                      pagination
                      fixedHeader
                      fixedHeaderScrollHeight="44vh"
                      persistTableHead
                      noDataComponent="Ingen rabatkoder i denne kategori endnu."
                      subHeader
                      subHeaderComponent={
                        <InputGroup className="w-100" style={{ maxWidth: "420px" }}>
                          <InputGroup.Text>Søg</InputGroup.Text>
                          <Form.Control
                            placeholder="Søg på title, kode, slug, status m.m."
                            value={postSearch}
                            onChange={(e) => setPostSearch(e.target.value)}
                          />
                        </InputGroup>
                      }
                    />
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
        </Tab>

        <Tab eventKey="articles" title="Artikler">
          <ArticleTab />
        </Tab>
      </Tabs>

      <Modal show={confirmModal.show} onHide={closeConfirm} centered>
        <Modal.Header closeButton>
          <Modal.Title>Bekræft handling</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Er du sikker på, at du vil køre <strong>{confirmModal.label}</strong>?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeConfirm}>
            Annuller
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              closeConfirm()
              confirmModal.onConfirm()
            }}
          >
            Bekræft
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}
